import { FormattedMessage, useIntl } from 'react-intl';
import SummaryItem from 'client/components/ui/SummaryItem/SummaryItem';
import WorkpackDetails from 'client/pages/WorkpacksPage/components/WorkpackDetails/WorkpackDetails';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import { electricitySymbologyFeatureFlag, enquiryFeatureFlag, printPreviewFeatureFlag } from 'client/utils/constants/frontEndConfig';
import WorkpackPrintPreview from '../../components/WorkpackPrintPreview/WorkpackPrintPreview';
import Status from 'client/components/aria/Status/Status';
import styles from './WorkpackSummary.module.scss';

const WorkpackSummary = ({ enquiry, showSummary, change, changePurpose, capabilities, aoi, featuresByAssetType = {}, onEditArea, hideChangeLinks = false }) => {
    const intl = useIntl();

    // Get purpose display text
    const getPurposeDisplay = () => {
        if (enquiry.purpose === 'prework') {
            return intl.formatMessage({ id: 'createWorkpackPage.purpose.prework' });
        } else if (enquiry.purpose === 'planned') {
            return intl.formatMessage({ id: 'createWorkpackPage.purpose.planned' });
        } else if (enquiry.purpose === 'emergency') {
            return intl.formatMessage({ id: 'createWorkpackPage.purpose.emergency' });
        } else {
            // For any other value (custom 'other' text), display it as-is
            return enquiry.purpose;
        }
    };

    // Get orientation display text
    const getOrientationDisplay = () => {
        if (!enquiry?.workpackOrientation) {
            return 'N/A';
        }
        // Extract orientation from full string (e.g., "A4 Landscape" -> "Landscape")
        const parts = enquiry.workpackOrientation.split(' ');
        return parts.length > 1 ? parts.slice(1).join(' ') : enquiry.workpackOrientation;
    };

    // Get paper size display text
    const getPaperSizeDisplay = () => {
        if (!enquiry?.workpackOrientation) {
            return 'N/A';
        }
        // Extract paper size from orientation (e.g., "A4 Landscape" -> "A4")
        const paperSize = enquiry.workpackOrientation.split(' ')[0];
        return paperSize || 'N/A';
    };

    // Get map scale display text
    const getMapScaleDisplay = () => {
        if (!enquiry?.workpackScale) {
            return 'N/A';
        }
        return `1:${enquiry.workpackScale}`;
    };

    return (
        <>
            {/* Purpose Section */}
            {enquiryFeatureFlag
                ? <>
                    <WorkpackDetails>
                        {intl.formatMessage({ id: 'workpackSummary.pdfType' })}
                    </WorkpackDetails>
                    <SummaryItem
                        title={intl.formatMessage({ id: 'workpackSummary.purpose' })}
                        showChangeOption={!hideChangeLinks && showSummary}
                        change={changePurpose}
                        data-testid="workpack-summary-purpose"
                    >
                        {getPurposeDisplay()}
                    </SummaryItem>

                    <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                </>
                : null
            }

            {/* Details Section */}
            <WorkpackDetails className={'govuk-!-margin-top-4'}>
                {intl.formatMessage({ id: 'workpackSummary.details' })}
            </WorkpackDetails>

            <SummaryItem
                title={intl.formatMessage({ id: 'workpackSummary.name' })}
                showChangeOption={!hideChangeLinks && showSummary}
                change={change}
                data-testid="workpack-summary-name"
            >
                {enquiry?.title || 'N/A'}
            </SummaryItem>
            <hr className="govuk-section-break govuk-section-break--l govuk-section-break--visible" />
            <SummaryItem
                title={intl.formatMessage({ id: 'workpackSummary.paperSize' })}
                showChangeOption={!hideChangeLinks && showSummary}
                change={change}
                data-testid="workpack-summary-paper-size"
            >
                {getPaperSizeDisplay()}
            </SummaryItem>
            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
            <SummaryItem
                title={intl.formatMessage({ id: 'workpackSummary.paperOrientation' })}
                showChangeOption={false}
                data-testid="workpack-summary-orientation"
            >
                {getOrientationDisplay()}
            </SummaryItem>
            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />

            <SummaryItem
                title={intl.formatMessage({ id: 'workpackSummary.mapScale' })}
                showChangeOption={!hideChangeLinks && showSummary}
                change={change}
                data-testid="workpack-summary-map-scale"
            >
                {getMapScaleDisplay()}
            </SummaryItem>
            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />

            {printPreviewFeatureFlag && aoi && (
                <>
                    <SummaryItem
                        title={intl.formatMessage({ id: 'workpackPrintPreview.title' })}
                        showChangeOption={!hideChangeLinks && showSummary}
                        change={change}
                        variant="stacked"
                        data-testid="workpack-summary-print-preview" >
                        <WorkpackPrintPreview
                            orientation={enquiry?.workpackOrientation}
                            scale={enquiry?.workpackScale}
                            aoi={aoi}
                            featuresByAssetType={featuresByAssetType}
                            onEditArea={onEditArea}
                            variant="summary"
                            data-testid="workpack-summary-print-preview"
                        />
                    </SummaryItem>
                    <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                </>
            )}

            {electricitySymbologyFeatureFlag && (
                <>
                    <SummaryItem
                        title={
                            <div className={styles.betaFeatureTitle}>
                                <FormattedMessage id='workpackSummary.electricityVoltageColourCoding' />
                                <Status className={styles.betaFeatureStatus} variant='blue'>
                                    <FormattedMessage id='optIn.betaFeature' />
                                </Status>
                            </div>
                        }
                        showChangeOption={!hideChangeLinks && showSummary}
                        change={change}
                        data-testid="workpack-summary-electricity-voltage-colour-coding"
                    >
                        {
                            enquiry?.electricityStylingOptIn === true
                                ? <FormattedMessage id='workpackSummary.electricityVoltageColourCoding.on' />
                                : <FormattedMessage id='workpackSummary.electricityVoltageColourCoding.off' />
                        }
                    </SummaryItem>
                    <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                </>
            )}
        </>
    );
};

WorkpackSummary.propTypes = {
    ...defaultAriaProps
};

WorkpackSummary.defaultProps = {
    showSummary: false
};

export default WorkpackSummary;
